import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  LeadsState,
  Lead,
  SummaryItem,
  LeadFormConfig,
} from "../../pages/Communications/utils/types";

const initialState: LeadsState = {
  summary: [],
  leads: [],
  selectedChatType: "all_chats",
  selectedLead: null,
  isLoading: false,
  error: null,
  pagination: {
    cursor: null,
    hasMore: true,
    isLoadingMore: false,
  },
  leadsFormConfig: [],
};

const leadsSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    setLeads: (
      state,
      action: PayloadAction<{
        summary: SummaryItem[];
        leads: Lead[];
        cursor?: string | null;
      }>
    ) => {
      state.leads = action.payload.leads;
      state.summary = action.payload.summary;
      state.error = null;
      state.pagination.cursor = action.payload.cursor || null;
      state.pagination.hasMore = !!action.payload.cursor;
    },
    appendLeads: (
      state,
      action: PayloadAction<{
        leads: Lead[];
        hasMore: boolean;
        cursor?: string | null;
      }>
    ) => {
      state.leads = [...state.leads, ...action.payload.leads];
      state.pagination.cursor = action.payload.cursor || null;
      state.pagination.hasMore = action.payload.hasMore;
      state.pagination.isLoadingMore = false;
      state.error = null;
    },
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.pagination.isLoadingMore = action.payload;
    },
    resetPagination: (state) => {
      state.pagination.cursor = null;
      state.pagination.hasMore = true;
      state.pagination.isLoadingMore = false;
    },
    setSelectedLead: (state, action: PayloadAction<Lead | null>) => {
      state.selectedLead = action.payload;
    },
    clearSelectedLead: (state) => {
      state.selectedLead = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setSelectedChatType: (state, action: PayloadAction<string | null>) => {
      state.selectedChatType = action.payload;
    },
    clearSelectedChatType: (state) => {
      state.selectedChatType = null;
    },
    clearLeads: (state) => {
      state.summary = [];
      state.leads = [];
      state.selectedLead = null;
      state.error = null;
      state.pagination = {
        cursor: null,
        hasMore: true,
        isLoadingMore: false,
      };
    },
    updateLead: (state, action: PayloadAction<Lead>) => {
      const leadIndex = state.leads.findIndex(
        (lead) => lead.leadId === action.payload.leadId
      );

      if (leadIndex !== -1) {
        state.leads[leadIndex] = {
          ...state.leads[leadIndex],
          ...action.payload,
        };
      }
    },
    addLead: (state, action: PayloadAction<Lead>) => {
      state.leads.unshift(action.payload);
    },
    updateOrAddLead: (state, action: PayloadAction<Lead>) => {
      const leadIndex = state.leads.findIndex(
        (lead) => lead.leadId === action.payload.leadId
      );

      if (leadIndex !== -1) {
        const [existingLead] = state.leads.splice(leadIndex, 1);

        state.leads.unshift({
          ...existingLead,
          ...action.payload,
        });
      } else {
        state.leads.unshift(action.payload);
      }
    },
    updateSelectedLead: (state, action: PayloadAction<Partial<Lead>>) => {
      if (state.selectedLead) {
        state.selectedLead = { ...state.selectedLead, ...action.payload };
      }
    },
    removeLeadById: (state, action: PayloadAction<string>) => {
      state.leads = state.leads.filter(
        (lead) => lead.leadId !== action.payload
      );
    },
    updateLeadName: (
      state,
      action: PayloadAction<{ leadId: string; name: string }>
    ) => {
      const { leadId, name } = action.payload;
      const leadIndex = state?.leads?.findIndex(
        (lead) => lead.leadId === leadId
      );
      if (leadIndex !== -1) {
        state.leads[leadIndex].leadName = name;
      }
    },
    setLeadsFormConfig: (state, action: PayloadAction<LeadFormConfig[]>) => {
      state.leadsFormConfig = action.payload;
    },
    clearLeadsFormConfig: (state) => {
      state.leadsFormConfig = [];
    },
  },
});

export const {
  setLeads,
  appendLeads,
  setLoadingMore,
  resetPagination,
  setSelectedLead,
  clearSelectedLead,
  setLoading,
  setError,
  clearLeads,
  updateLead,
  addLead,
  updateOrAddLead,
  updateSelectedLead,
  removeLeadById,
  updateLeadName,
  setSelectedChatType,
  clearSelectedChatType,
  setLeadsFormConfig,
  clearLeadsFormConfig,
} = leadsSlice.actions;

export const selectSelectedLead = (state: { leads: LeadsState }) =>
  state.leads.selectedLead;
export const selectLeads = (state: { leads: LeadsState }) => state.leads.leads;
export const selectSelectedLeadId = (state: { leads: LeadsState }) =>
  state.leads.selectedLead?.lead_id || null;
export const selectIsLoading = (state: { leads: LeadsState }) =>
  state.leads.isLoading;
export const selectError = (state: { leads: LeadsState }) => state.leads.error;
export const selectPagination = (state: { leads: LeadsState }) =>
  state.leads.pagination;
export const selectHasMore = (state: { leads: LeadsState }) =>
  state.leads.pagination.hasMore;
export const selectIsLoadingMore = (state: { leads: LeadsState }) =>
  state.leads.pagination.isLoadingMore;
export const selectLeadsFormConfig = (state: { leads: LeadsState }) =>
  state.leads.leadsFormConfig;

export default leadsSlice;
